/* eslint-disable jira/naming/disallow-filenames */
// eslint-disable-next-line jira/filename-case
import React from 'react';
import {
	JiraIcon,
	JiraServiceManagementIcon,
	JiraProductDiscoveryIcon,
	CustomerServiceManagementIcon,
	type LogoProps,
} from '@atlaskit/logo';
import type { ProductName } from '@atlassian/jira-shared-types/src/product-name.tsx';
/* eslint-disable jira/i18n/no-export-define-message */
/* eslint-disable jira/filename-case */
// eslint-disable-next-line no-restricted-imports
import { defineMessage } from '@atlassian/jira-intl';

const jiraLogoLabel = defineMessage({
	// eslint-disable-next-line @atlassian/tangerine/i18n/messages-are-spelled-correctly
	defaultMessage: 'Go to your Jira homepage',
	id: 'atlassian-navigation.product-home.jira-logo-label',
	description:
		'Accessible label for the Jira logo in the top navigation bar that links to the Jira homepage',
});

const jsmLogoLabel = defineMessage({
	// eslint-disable-next-line @atlassian/tangerine/i18n/messages-are-spelled-correctly
	defaultMessage: 'Go to your Jira Service Management homepage',
	id: 'atlassian-navigation.product-home.jsm-logo-label',
	description:
		'Accessible label for the Jira Service Management logo in the top navigation bar that links to the JSM homepage',
});

const jpdLogoLabel = defineMessage({
	// eslint-disable-next-line @atlassian/tangerine/i18n/messages-are-spelled-correctly
	defaultMessage: 'Go to your Jira Product Discovery homepage',
	id: 'atlassian-navigation.product-home.jpd-logo-label',
	description:
		'Accessible label for the Jira Product Discovery logo in the top navigation bar that links to the JPD homepage',
});

const csmLogoLabel = defineMessage({
	// eslint-disable-next-line @atlassian/tangerine/i18n/messages-are-spelled-correctly
	defaultMessage: 'Go to your Customer Service Management homepage',
	id: 'atlassian-navigation.product-home.csm-logo-label',
	description:
		'Accessible label for the Customer Service Management logo in the top navigation bar that links to the CSM homepage',
});

export const iconMap: {
	readonly jira: {
		readonly icon: ({
			size,
			shouldUseNewLogoDesign,
			...props
		}: {
			size?: 'medium' | 'large' | 'small' | 'xxsmall' | 'xsmall' | 'xlarge';
			appearance?: 'brand' | 'neutral' | 'inverse';
			textColor?: string;
			iconColor?: string;
			label?: string;
			testId?: string;
			shouldUseNewLogoDesign?: boolean;
		}) => React.JSX.Element;
		readonly name: 'Jira';
	};
	readonly serviceDesk: {
		readonly icon: ({
			size,
			shouldUseNewLogoDesign,
			...props
		}: {
			size?: 'medium' | 'large' | 'small' | 'xxsmall' | 'xsmall' | 'xlarge';
			appearance?: 'brand' | 'neutral' | 'inverse';
			textColor?: string;
			iconColor?: string;
			label?: string;
			testId?: string;
			shouldUseNewLogoDesign?: boolean;
		}) => React.JSX.Element;
		readonly name: 'Jira Service Management';
	};
	readonly 'product-discovery': {
		readonly icon: ({
			size,
			shouldUseNewLogoDesign,
			...props
		}: {
			size?: 'medium' | 'large' | 'small' | 'xxsmall' | 'xsmall' | 'xlarge';
			appearance?: 'brand' | 'neutral' | 'inverse';
			textColor?: string;
			iconColor?: string;
			label?: string;
			testId?: string;
			shouldUseNewLogoDesign?: boolean;
		}) => React.JSX.Element;
		readonly name: 'Jira Product Discovery';
	};
	readonly 'customer-service': {
		readonly icon: ({
			size,
			...props
		}: {
			size?: 'medium' | 'large' | 'small' | 'xxsmall' | 'xsmall' | 'xlarge';
			appearance?: 'brand' | 'neutral' | 'inverse';
			textColor?: string;
			iconColor?: string;
			label?: string;
			testId?: string;
			shouldUseNewLogoDesign?: boolean;
		}) => React.JSX.Element;
		readonly name: 'Customer Service Management';
	};
} = {
	jira: { icon: JiraIcon, name: 'Jira' },
	serviceDesk: {
		icon: JiraServiceManagementIcon,
		name: 'Jira Service Management',
	},
	'product-discovery': {
		icon: JiraProductDiscoveryIcon,
		name: 'Jira Product Discovery',
	},
	'customer-service': {
		icon: CustomerServiceManagementIcon,
		name: 'Customer Service Management',
	},
} as const;

/**
 * Due to typescript limitations (props type clashes), we need to handle the icon and logo components differently for the
 * new and old navigation apps. This function returns the appropriate components based on the product name.
 */
export function getProductIcon(
	productName: ProductName,
	testIdPrefix?: string,
	label?: string,
): {
	icon: (props: {
		size?: 'medium' | 'large' | 'small' | 'xxsmall' | 'xsmall' | 'xlarge';
		appearance?: 'brand' | 'neutral' | 'inverse';
		textColor?: string;
		iconColor?: string;
		label?: string;
		testId?: string;
		shouldUseNewLogoDesign?: boolean;
	}) => React.JSX.Element;
	name:
		| 'Jira'
		| 'Jira Service Management'
		| 'Jira Product Discovery'
		| 'Customer Service Management';
} {
	const productKey = productName === 'core' || productName === 'software' ? 'jira' : productName;

	const { icon: NewIconComponent, name: logoName } = iconMap[productKey];

	const createComponent =
		<P extends LogoProps>(
			Component: React.ComponentType<P>,
			type: 'icon' | 'logo',
			shouldUseNewLogoDesign?: boolean,
		) =>
		(props: P) => {
			const additionalProps: Partial<P> & { shouldUseNewLogoDesign?: boolean } = {};
			// Nav v3 sets a testId, Nav v4 sets a label
			if (testIdPrefix) {
				additionalProps.testId = `${testIdPrefix}-${type}`;
			}
			if (label) {
				additionalProps.label = label;
			}
			additionalProps.shouldUseNewLogoDesign = shouldUseNewLogoDesign;
			// Casting required due to complexities of the different component prop types
			return (
				<Component
					{...props}
					// oxlint-disable-next-line typescript/consistent-type-assertions
					{...(additionalProps as P)}
				/>
			);
		};

	const newIcon = createComponent<LogoProps>(NewIconComponent, 'icon', true);

	return { icon: newIcon, name: logoName };
}

// formatMessage is intentionally an any type due to absence of easily importable type, and because actual
// type definition contains any as well!
// oxlint-disable-next-line @typescript-eslint/no-explicit-any
export function getLogoLabel(formatMessage: any, productName: ProductName): string {
	let label = '';
	if (productName === 'serviceDesk') {
		label = formatMessage(jsmLogoLabel);
	} else if (productName === 'product-discovery') {
		label = formatMessage(jpdLogoLabel);
	} else if (productName === 'customer-service') {
		label = formatMessage(csmLogoLabel);
	} else {
		label = formatMessage(jiraLogoLabel);
	}
	return label;
}
